import {
  SecurityFocusEventType as TrackSecurityFocusEventType,
  type SecurityFocusEvent,
  type Blueprint,
  type Spidr,
  type SecurityFocusEvents,
} from '@rtkwlf/track-public';

const eventType = {
  ITEM: 'ITEM',
  GROUP: 'GROUP',
};

/**
 * Blueprint timeline event
 */
type BlueprintEventType<T> = T &
  SecurityFocusEvent & {
    type: (typeof TrackSecurityFocusEventType)['Blueprint'];
    eventType: 'ITEM';
  };

/**
 * SPiDR timeline event
 */
type SpidrEventType<T> = T &
  SecurityFocusEvent & {
    type: (typeof TrackSecurityFocusEventType)['SpiDr'];
    eventType: 'ITEM';
  };

/**
 * Detour timeline event
 */
type DetourEventType = SecurityFocusEvent & {
  type: (typeof TrackSecurityFocusEventType)['Detour'];
  eventType: 'ITEM';
};

/**
 * SecurityFocus timeline event
 */
type SecurityFocusEventType = SecurityFocusEvent & {
  type: (typeof TrackSecurityFocusEventType)['SecurityFocus'];
  eventType: 'ITEM';
};

/**
 * Adhoc timeline event
 */
type AdhocEventType = SecurityFocusEvent & {
  type: (typeof TrackSecurityFocusEventType)['Adhoc'];
  eventType: 'ITEM';
};

/**
 * Timeline item events
 */
export type SecurityFocusTimelineItemType =
  | BlueprintEventType<Blueprint>
  | SpidrEventType<Spidr>
  | AdhocEventType
  | DetourEventType
  | SecurityFocusEventType;

/**
 * Timeline group with items
 */
type SecurityFocusTimelineGroupType = {
  name: string;
  eventType: 'GROUP';
  items: SecurityFocusTimelineItemType[];
};

/**
 * Arguements to get timeline data
 */
type GetSecurityFocusTimelineDataArgs = {
  focus: {
    blueprints: Record<string, Blueprint>;
    spidrs: Record<string, Spidr>;
  };
  events: SecurityFocusEvents;
};

/**
 * Return type for timeline data
 */
type GetSecurityFocusTimelineDataReturn = (
  | SecurityFocusTimelineItemType
  | SecurityFocusTimelineGroupType
)[];

export const getSecurityFocusTimelineData = ({
  focus,
  events,
}: GetSecurityFocusTimelineDataArgs) => {
  const allEvents: GetSecurityFocusTimelineDataReturn = [];
  for (const [i, [groupName, groupItems]] of Object.entries(events).entries()) {
    const group: SecurityFocusTimelineGroupType = {
      name: groupName,
      eventType: 'GROUP',
      items: [],
    };
    for (const [j, item] of groupItems.entries()) {
      if (
        i === 0 &&
        j === 0 &&
        (item.type === TrackSecurityFocusEventType.SecurityFocus ||
          item.type === TrackSecurityFocusEventType.Detour)
      ) {
        allEvents.push({ ...item, eventType: eventType.ITEM });
        continue;
      }

      switch (item.type) {
        case TrackSecurityFocusEventType.SecurityFocus:
        case TrackSecurityFocusEventType.Detour:
        case TrackSecurityFocusEventType.Adhoc: {
          group.items.push({ ...item, eventType: eventType.ITEM });
          break;
        }
        case TrackSecurityFocusEventType.SpiDr: {
          if (Object.hasOwn(focus.spidrs, item.id)) {
            group.items.push({
              ...item,
              ...focus.spidrs[item.id],
              eventType: eventType.ITEM,
            });
          }
          break;
        }
        case TrackSecurityFocusEventType.Blueprint: {
          if (Object.hasOwn(focus.blueprints, item.id)) {
            group.items.push({
              // blueprint also has a `type` field, but we don't care about it so the event type takes priority
              ...focus.blueprints[item.id],
              ...item,
              eventType: eventType.ITEM,
            });
          }
          break;
        }
      }
    }

    if (group.items.length > 0) {
      allEvents.push(group);
    }
  }

  return allEvents;
};
